/**
 * Utility functions for managing mobile address bar visibility
 */

/**
 * Hides the mobile address bar by scrolling the page slightly
 * This is a common technique to create a more immersive mobile experience
 */
export const hideAddressBar = (): void => {
  if (typeof window === 'undefined') return;
  
  // Only apply on mobile devices
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (!isMobile) return;
  
  // Check if we're in landscape mode
  const isLandscape = window.innerHeight < window.innerWidth;
  if (!isLandscape) return;
  
  // Store the current scroll position
  const currentScrollY = window.scrollY;
  
  // Scroll down by 1px to hide the address bar
  window.scrollTo(0, currentScrollY + 1);
  
  // Immediately scroll back to the original position
  // This tricks the browser into hiding the address bar
  setTimeout(() => {
    window.scrollTo(0, currentScrollY);
  }, 0);
};

/**
 * Forces the address bar to hide by using a more aggressive approach
 * This method works by temporarily making the page taller than the viewport
 */
export const forceHideAddressBar = (): void => {
  if (typeof window === 'undefined') return;
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (!isMobile) return;
  
  const isLandscape = window.innerHeight < window.innerWidth;
  if (!isLandscape) return;
  
  // Get the current viewport height
  const viewportHeight = window.innerHeight;
  const documentHeight = document.documentElement.scrollHeight;
  
  // If the document is not taller than the viewport, make it so
  if (documentHeight <= viewportHeight) {
    // Add a temporary element to make the page taller
    const tempElement = document.createElement('div');
    tempElement.style.height = `${viewportHeight + 1}px`;
    tempElement.style.position = 'absolute';
    tempElement.style.top = '0';
    tempElement.style.left = '0';
    tempElement.style.width = '1px';
    tempElement.style.visibility = 'hidden';
    tempElement.style.pointerEvents = 'none';
    tempElement.id = 'address-bar-hider';
    
    document.body.appendChild(tempElement);
    
    // Scroll to hide the address bar
    window.scrollTo(0, 1);
    
    // Remove the temporary element after a short delay
    setTimeout(() => {
      const element = document.getElementById('address-bar-hider');
      if (element) {
        element.remove();
      }
    }, 100);
  } else {
    // Document is already tall enough, just scroll
    window.scrollTo(0, 1);
  }
};

/**
 * Sets up automatic address bar hiding for mobile landscape mode
 * This should be called when entering KMDeck mode
 */
export const setupAddressBarHiding = (): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (!isMobile) return () => {};
  
  let hideTimeout: NodeJS.Timeout;
  
  const hideAddressBarOnResize = () => {
    // Clear any existing timeout
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }
    
    // Check if we're in landscape mode
    const isLandscape = window.innerHeight < window.innerWidth;
    
    if (isLandscape) {
      // Hide address bar after a short delay to ensure orientation change is complete
      hideTimeout = setTimeout(() => {
        forceHideAddressBar();
      }, 300);
    }
  };
  
  // Set up event listeners
  window.addEventListener('orientationchange', hideAddressBarOnResize);
  window.addEventListener('resize', hideAddressBarOnResize);
  
  // Initial call
  hideAddressBarOnResize();
  
  // Return cleanup function
  return () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }
    window.removeEventListener('orientationchange', hideAddressBarOnResize);
    window.removeEventListener('resize', hideAddressBarOnResize);
  };
};

/**
 * Checks if the device is in landscape mode
 */
export const isLandscapeMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerHeight < window.innerWidth;
};

/**
 * Checks if the device is mobile and in landscape mode
 */
export const isMobileLandscape = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isLandscape = window.innerHeight < window.innerWidth;
  
  return isMobile && isLandscape;
};
